import I18n from 'i18n-js';
import React from 'react';

import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { I18N as Translate } from '@reverbdotcom/commons/src/components/translate';
import FormGroupWithRadio from '@reverbdotcom/commons/src/components/form_group_with_radio';
import { RCTag } from '@reverbdotcom/commons/src/rc/RCTag';
import SiteModule from '@reverbdotcom/commons/src/components/site_module';
import { EVERYWHERE_CODE } from '@reverbdotcom/commons/src/constants';

import EditShippingRateCardManualPrices, { IShippingRate } from './edit_shipping_rate_card_manual_prices';
import EditShippingRateCardPackage, { CARRIER_CALC_MAX_LENGTH_INCHES, CARRIER_CALC_MAX_WEIGHT_POUNDS } from './edit_shipping_rate_card_package';
import { core_apimessages_ShippingLabelPackage } from '../../schemas/rql';
import URLHelpers from './url_helpers';

export enum FormContext {
  SHIPPING_PROFILE = 'shippingProfile',
  LISTING = 'listing',
}

enum ShippingMode {
  CUSTOM = 'custom', // user manually inputs a standard shipping price (with optional combine & save + expedited prices)
  CARRIER_CALCULATED = 'carrierCalculated', // user inputs package dimensions, reverb figures out the shipping cost
  FREE = 'free', // user selects simple free shipping (no combine+save / expedited; no user input required)
}

// This component currently renders inputs within haml forms, and uses input names expected by shipping profile/listing rails controllers.
// Once shipping rates can be submitted via a mutation, this naming can be stripped out/simplified.
export const INPUT_NAME_PREFIXES = {
  [FormContext.SHIPPING_PROFILE]: 'shipping_profile[shipping][rates][]',
  [FormContext.LISTING]: 'product[shipping][rates][]',
};

interface IProps {
  shippingRate?: IShippingRate;
  formContext: FormContext;
  currency: string;
  handleRemoveShippingRate?: (IShippingRate) => void;
  handleEditShippingRate?: (IShippingRate) => void;
  allowedToShipCarrierCalculated: boolean;
  inCarrierCalculatedMode: boolean;
  allowedToShipExpedited: boolean;
  setIsInCarrierCalculatedMode: (boolean) => void;
  shopAddressSummary?: string;
  shippingPackage?: core_apimessages_ShippingLabelPackage;
}

function isZeroAmount(input) {
  if (input === '' || input === undefined || input === null) { return false; }

  return Number(input) === 0;
}

function existingRateHasFreeStandardShipping(shippingRate: IShippingRate): boolean {
  return isZeroAmount(shippingRate?.rate?.amount) &&
    !shippingRate?.incrementalRate?.amount &&
    !shippingRate?.expeditedRate?.amount;
}

function CarrierCalculatedDescription() {
  return (
    <ul className="shipping-rate-card__carrier__calculated__list__group">
      <li className="shipping-rate-card__carrier__calculated__list__item">
        {I18n.t('discovery.dashboard.selling.shippingRates.shippingModeRadios.carrierCalculated.description.dimensionLimits', {
          maxLengthInches: String(CARRIER_CALC_MAX_LENGTH_INCHES),
          maxWeightPounds: String(CARRIER_CALC_MAX_WEIGHT_POUNDS),
        })}
      </li>

      <li className="shipping-rate-card__carrier__calculated__list__item">
        {I18n.t('discovery.dashboard.selling.shippingRates.shippingModeRadios.carrierCalculated.description.willSendLabel')}
      </li>

      <li className="shipping-rate-card__carrier__calculated__list__item">
        <Translate
          text="discovery.dashboard.selling.shippingRates.shippingModeRadios.carrierCalculated.description.bySelecting"
        >
          <CoreLink to={URLHelpers.legalPagePath('calculated-shipping-terms-and-conditions')} target="_blank">
            {I18n.t('discovery.dashboard.selling.shippingRates.shippingModeRadios.carrierCalculated.description.terms')}
          </CoreLink>
        </Translate>
      </li>
    </ul>
  );
}

interface ICollapsibleShippingRadioProps {
  forMode: ShippingMode;
  badgeText: string;
  shippingRate: IShippingRate;
  handleModeUpdate: (string) => void;
  children: any;
  selected: boolean
  shopAddressSummary?: string;
}

// A an expandable radio toggle card for each shipping mode, which renders child inputs when active
function CollapsibleShippingRadio({
  forMode,
  selected,
  shippingRate,
  badgeText,
  handleModeUpdate,
  children,
  shopAddressSummary,
}: ICollapsibleShippingRadioProps) {
  const inputName = `${forMode}_radio_${shippingRate.regionCode}`;
  const forCarrierCalculatedMode = forMode === ShippingMode.CARRIER_CALCULATED;
  return (
    <SiteModule
      footerContent={forCarrierCalculatedMode && <CarrierCalculatedDescription />}
    >
      <FormGroupWithRadio
        inputName={inputName}
        label={(
          <label htmlFor={inputName}>
            <span className="shipping-rate-card__shipping__mode__title">
              {I18n.t(`discovery.dashboard.selling.shippingRates.shippingModeRadios.${forMode}.title`)}
            </span>

            {badgeText && <RCTag variant="highlight">{badgeText}</RCTag>}
          </label>
        )}
        helpText={(
          <div>
            <p className="shipping-rate-card__carrier__calculated__help__text">
              {forCarrierCalculatedMode && (
                <Translate
                  text="discovery.dashboard.selling.shippingRates.shippingModeRadios.carrierCalculated.helpText"
                  args={{ shopAddressSummary }}
                >
                  <CoreLink to={URLHelpers.shopEditPath({ anchor: 'edit_shop_location' })} target="_blank">
                    {I18n.t('discovery.dashboard.selling.shippingRates.shippingModeRadios.carrierCalculated.shopAddress')}
                  </CoreLink>
                </Translate>
              )}

              {!forCarrierCalculatedMode && I18n.t(`discovery.dashboard.selling.shippingRates.shippingModeRadios.${forMode}.helpText`)}
            </p>
          </div>
        )}
        value={forMode}
        checked={selected}
        updateField={() => handleModeUpdate(forMode)}
      />

      {selected && children && (
        <div className="shipping-rate-collapsible__radio__content">
          {children}
        </div>
      )}
    </SiteModule>
  );
}

export function EditShippingRateCard({
  shippingRate,
  allowedToShipExpedited,
  handleRemoveShippingRate,
  handleEditShippingRate,
  formContext,
  currency,
  allowedToShipCarrierCalculated,
  inCarrierCalculatedMode,
  setIsInCarrierCalculatedMode,
  shopAddressSummary,
  shippingPackage,
}: IProps) {
  const rateCardRef = React.useRef<HTMLInputElement>();
  const isEverywhereElseRegion = shippingRate.regionCode === EVERYWHERE_CODE;
  const [isFreeModeSelected, setIsFreeModeSelected] = React.useState(existingRateHasFreeStandardShipping(shippingRate));

  function handleModeUpdate(newMode) {
    if (allowedToShipCarrierCalculated) {
      setIsInCarrierCalculatedMode(newMode === ShippingMode.CARRIER_CALCULATED);
    }
    setIsFreeModeSelected(newMode === ShippingMode.FREE);
  }

  let currentMode = ShippingMode.CUSTOM;
  if (allowedToShipCarrierCalculated && inCarrierCalculatedMode) {
    currentMode = ShippingMode.CARRIER_CALCULATED;
  } else if (isFreeModeSelected) {
    currentMode = ShippingMode.FREE;
  }

  return (
    <div className="shipping-rate-card" ref={rateCardRef}>
      <header className="shipping-rate-card__header">
        <div className="shipping-rate-card__titleblock">
          <h4 className="shipping-rate-card__title">
            {isEverywhereElseRegion && (
              <span className="icon-l-globe">
                {I18n.t('discovery.dashboard.selling.shippingRates.everywhereElse')}
              </span>)}

            {!isEverywhereElseRegion && shippingRate.regionName}
          </h4>
          {isEverywhereElseRegion && (
            <p className="shipping-rate-card__subtitle">
              {I18n.t('discovery.dashboard.selling.shippingRates.everywhereElseTip')}
            </p>
          )}
        </div>
        <div className="shipping-rate-card__remove">
          {shippingRate.isRequired && (<span className="form-group__tag">{I18n.t('commons.formGroup.required')}</span>)}

          {!shippingRate.isRequired && (
            <button
              className="d-block button-as-link size-80 color-red"
              onClick={() => handleRemoveShippingRate(shippingRate)}
              type="button"
            >
              {I18n.t('discovery.dashboard.selling.shippingRates.rateFields.removeRate')}
            </button>
          )}
        </div>
      </header>
      <div className="shipping-rate-card__main">
        <input
          data-region-type="true"
          hidden
          readOnly
          type="hidden"
          name={`${INPUT_NAME_PREFIXES[formContext]}[region_type]`}
          value={shippingRate.regionType}
        />
        <input
          data-region-code="true"
          hidden
          readOnly
          type="hidden"
          name={`${INPUT_NAME_PREFIXES[formContext]}[region_code]`}
          value={shippingRate.regionCode}
        />
        {currentMode === ShippingMode.FREE && (
          <input
            data-automatic-free="true"
            hidden
            readOnly
            type="hidden"
            name={`${INPUT_NAME_PREFIXES[formContext]}[rate]`}
            value="0"
          />
        )}

        <p><strong>{I18n.t('discovery.dashboard.selling.shippingRates.optionsHeader')}</strong></p>

        <CollapsibleShippingRadio
          data-free-mode-radio
          forMode={ShippingMode.FREE}
          selected={currentMode === ShippingMode.FREE}
          shippingRate={shippingRate}
          handleModeUpdate={handleModeUpdate}
          badgeText={I18n.t(`discovery.dashboard.selling.shippingRates.shippingModeRadios.${ShippingMode.FREE}.badgeText`)}
        >
        </CollapsibleShippingRadio>

        <CollapsibleShippingRadio
          data-custom-mode-radio
          forMode={ShippingMode.CUSTOM}
          selected={currentMode === ShippingMode.CUSTOM}
          shippingRate={shippingRate}
          handleModeUpdate={handleModeUpdate}
          badgeText={
            allowedToShipCarrierCalculated && I18n.t(`discovery.dashboard.selling.shippingRates.shippingModeRadios.${ShippingMode.CUSTOM}.badgeText`)
          }
        >
          <EditShippingRateCardManualPrices
            formContext={formContext}
            shippingRate={shippingRate}
            handleEditShippingRate={handleEditShippingRate}
            currency={currency}
            allowedToShipExpedited={allowedToShipExpedited}
          />
        </CollapsibleShippingRadio>

        {allowedToShipCarrierCalculated && (
          <CollapsibleShippingRadio
            data-carrier-calculated-mode-radio
            forMode={ShippingMode.CARRIER_CALCULATED}
            selected={currentMode === ShippingMode.CARRIER_CALCULATED}
            shippingRate={shippingRate}
            handleModeUpdate={handleModeUpdate}
            badgeText={I18n.t(`discovery.dashboard.selling.shippingRates.shippingModeRadios.${ShippingMode.CARRIER_CALCULATED}.badgeText`)}
            shopAddressSummary={shopAddressSummary}
          >
            <EditShippingRateCardPackage
              shippingPackage={shippingPackage}
              setIsInCarrierCalculatedMode={(v) => {
                if (inCarrierCalculatedMode && v === false) {
                  // If a child component triggers an exit from carrier-calc mode, scroll to the top of this rate card
                  rateCardRef.current.scrollIntoView();
                }

                setIsInCarrierCalculatedMode(v);
              }}
            />
          </CollapsibleShippingRadio>
        )}
      </div>
    </div>
  );
}

export default EditShippingRateCard;
